import React from 'react';
import { Link } from 'react-router-dom';
import { useRoundResults } from '../../hooks/useRounds';
import { usePassage } from '../../hooks/usePassages';
import { useContributionStatus } from '../../hooks/useSubmissions';
import { useDetails } from '../../hooks/useDetails';
import { Card, CardBody, Button, LoadingSpinner } from '../common';
import { PassageWithAttribution } from '../voting/PassageWithAttribution';
import { PromptDisplay } from './PromptDisplay';

const AwardCard = ({ title, icon, recipient, amount, highlight = false }) => (
  <div className={`
    flex items-center gap-4 p-4 rounded-xl border-2
    ${highlight
      ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-300'
      : 'bg-gray-50 border-gray-200'}
  `}>
    <span className="text-3xl">{icon}</span>
    <div className="flex-1">
      <p className="text-sm font-medium text-gray-500">{title}</p>
      <p className="text-lg font-bold text-gray-900">{recipient}</p>
    </div>
    <div className="text-right">
      <p className="text-2xl font-bold text-primary-600">+{amount}</p>
      <p className="text-xs text-gray-500">Influence</p>
    </div>
  </div>
);

const VoteResultRow = ({ voter, isTop = false }) => (
  <div className={`
    flex items-center justify-between py-2 px-3 rounded-lg
    ${isTop ? 'bg-yellow-50' : 'hover:bg-gray-50'}
  `}>
    <div className="flex items-center gap-2">
      {isTop && <span className="text-lg">🏆</span>}
      <span className="font-medium text-gray-900">{voter.displayName}</span>
    </div>
    <div className="flex items-center gap-4">
      <span className="text-sm text-gray-500">
        {voter.voteCount} vote{voter.voteCount !== 1 ? 's' : ''}
      </span>
      {voter.distributionAmount > 0 && (
        <span className="text-sm font-semibold text-green-600">
          +{voter.distributionAmount}
        </span>
      )}
    </div>
  </div>
);

export const RoundCompleteView = ({
  gameId,
  roundId,
  round,
  isGM,
  onStartNewRound
}) => {
  const { data: results, isLoading: resultsLoading } = useRoundResults(gameId, roundId);
  const { data: passageData, isLoading: passageLoading } = usePassage(gameId, roundId);
  const { data: contributionData } = useContributionStatus(gameId, roundId);
  const { data: detailsData } = useDetails(gameId, roundId);

  // Find the winning detail (active status = not rejected)
  const details = detailsData?.details || [];
  const winningDetail = details.find(d => d.status === 'active');

  if (resultsLoading || passageLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const passage = passageData?.passage;
  const mvp = results?.mvp;
  const gmPick = results?.gmPick;
  const votes = results?.votes || [];

  // Build participants list for attribution
  const participants = contributionData?.players?.map(p => ({
    id: p.id,
    displayName: p.displayName,
  })) || [];

  return (
    <div className="space-y-6">
      {/* Round Complete Header */}
      <div className="text-center py-6 bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl border-2 border-primary-200">
        <h2 className="text-2xl font-bold text-primary-800">Round {round?.number} Complete!</h2>
        <p className="text-primary-600 mt-1">The story continues...</p>
      </div>

      {/* Awards Ribbon */}
      <div className="grid md:grid-cols-2 gap-4">
        {mvp && (
          <AwardCard
            title="MVP - Most Votes"
            icon="🏆"
            recipient={mvp.displayName}
            amount={results?.distribution?.[mvp.userId] || 0}
            highlight={true}
          />
        )}
        {gmPick && (
          <AwardCard
            title="GM's Pick"
            icon="⭐"
            recipient={gmPick.displayName}
            amount={gmPick.amount}
            highlight={true}
          />
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Main Content - Passage */}
        <div className="md:col-span-2">
          {/* GM Prompt as epigraph */}
          {round?.prompt && (
            <div className="mb-4">
              <PromptDisplay prompt={round.prompt} />
            </div>
          )}

          {/* Winning Detail */}
          {winningDetail && (
            <div className="mb-4 bg-amber-50 border-2 border-amber-400 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-sm">!</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-800 mb-1">
                    Winning Detail
                  </h3>
                  <p className="text-amber-900 font-medium">
                    "{winningDetail.content}"
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    By {winningDetail.authorUserId?.displayName || 'Unknown'} · {winningDetail.totalBid || 1} bid{(winningDetail.totalBid || 1) !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </div>
          )}

          <Card>
            <CardBody>
              <h3 className="font-semibold text-lg mb-4">Final Passage</h3>
              {passage ? (
                <PassageWithAttribution
                  passage={passage}
                  participants={participants}
                />
              ) : (
                <p className="text-gray-500 italic">No passage available.</p>
              )}
            </CardBody>
          </Card>
        </div>

        {/* Sidebar - Vote Results */}
        <div className="space-y-4">
          <Card>
            <CardBody>
              <h3 className="font-semibold mb-3">Vote Results</h3>
              {votes.length > 0 ? (
                <div className="space-y-1">
                  {votes.map((voter, index) => (
                    <VoteResultRow
                      key={voter.userId}
                      voter={voter}
                      isTop={index === 0}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No votes were cast.</p>
              )}
            </CardBody>
          </Card>

          {/* Pot Distribution Summary */}
          <Card>
            <CardBody>
              <h3 className="font-semibold mb-3">Pot Distribution</h3>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Total Pot</span>
                <span className="font-bold">{results?.pot || 0} Influence</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                MVP receives 1st place, GM Pick receives 2nd place in the cascading distribution.
              </p>
            </CardBody>
          </Card>

          {/* GM Action */}
          {isGM && (
            <Card>
              <CardBody>
                <h3 className="font-semibold mb-3">GM Controls</h3>
                <div className="space-y-2">
                  <Button
                    className="w-full"
                    onClick={onStartNewRound}
                  >
                    Start New Round
                  </Button>
                  <Link to={`/games/${gameId}`}>
                    <Button
                      variant="secondary"
                      className="w-full"
                    >
                      Manage Game
                    </Button>
                  </Link>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Player Waiting Message */}
          {!isGM && (
            <div className="text-center py-4 text-gray-500 text-sm">
              Waiting for GM to start the next round...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
